import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Collections, Window } from '@local/client-contracts';
import { isEmbed, isMac, isNativeWindow } from '@local/common-web';
import { isKey } from '@local/ts-infra';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EmbedService } from '@shared/embed.service';
import { EventsService, WindowService } from '@shared/services';
import { AppService } from '@shared/services/app.service';
import { BreadcrumbsService } from '@shared/services/breadcrumbs.service';
import { RouterService } from '@shared/services/router.service';
import { TitleBarService } from '@shared/services/title-bar.service';
import { windowSizeObserver } from '@shared/utils';
import { BehaviorSubject, firstValueFrom, fromEvent, Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { WorkspacesService } from 'src/app/bar/services';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { HomePageService } from 'src/app/bar/services/home-page.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { NavTreeService } from 'src/app/bar/services/nav-tree.service';
import { CHAT_PAGE_PATH } from 'src/app/bar/utils/constants';

type ActionButtonType = 'preferences' | 'close' | 'openNewWindow';
type ActionHeaderButton = {
  type: ActionButtonType;
  icon: string;
  toolTip: string;
};

@UntilDestroy()
@Component({
  selector: 'app-title-bar',
  templateUrl: './app-title-bar.component.html',
  styleUrls: ['./app-title-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTitleBarComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostListener('mouseenter', ['$event'])
  public onMouseEnter(event: MouseEvent) {
    this.mouseOver = true;
    this.cdr.markForCheck();
  }
  @HostListener('mouseleave', ['$event'])
  public onMouseLeave(event: MouseEvent) {
    this.mouseOver = false;
    this.cdr.markForCheck();
  }

  @HostBinding('class.draggable-disabled')
  get bgColor() {
    return this.isMac && this.isNative && this.routerService.active === 'home';
  }

  @ViewChild('breadcrumbsRef', { read: ElementRef }) breadcrumbs: ElementRef;
  @ViewChild('collectionHeaderLineRef', { read: ElementRef }) collectionHeaderLineRef: ElementRef;

  private readonly destroy$ = new Subject();
  private readonly ASSISTANT_PAGE = 'admin/assistant';
  readonly ACTION_HEADER_BUTTONS: ActionHeaderButton[] = [
    // { type: 'preferences', icon: 'icon-cog2', toolTip: 'Preferences' },
  ];

  @Input() controls: Partial<Array<'maximize' | 'minimize' | 'close' | 'pin'>> = [];

  pinned: boolean;
  maximized: boolean;
  isOwnerOrAdmin: boolean;
  mouseOver: boolean;
  minimalControls$: Observable<boolean>;
  altDown$ = new BehaviorSubject<boolean>(false);
  altDown: boolean;
  isMac = isMac();
  isNative = isNativeWindow();

  disableMinimize$: Observable<boolean>;
  invertMaximizeIcon$: Observable<boolean>;
  readonly settingsPage = 'settings';
  showFullMode = false;
  currentLocation: string;
  isEmbed = false;
  groupName: string;
  homeStickyHeader = false;
  embedInline: boolean;
  allowBackButtonsInEmbed: boolean;
  currentCollection: Collections.Collection;

  hideBreadcrumbsPath: boolean;

  showArrowsBreadcrumbs: boolean;
  isChatPage = false;
  constructor(
    public service: TitleBarService,
    private window: WindowService,
    private cdr: ChangeDetectorRef,
    private popupService: PopupService,
    private eventsService: EventsService,
    public hubService: HubService,
    private routerService: RouterService,
    private appService: AppService,
    private navTreeService: NavTreeService,
    private workspaceService: WorkspacesService,
    private embedService: EmbedService,
    private homePageService: HomePageService,
    public collectionsService: CollectionsService,
    private breadcrumbsService: BreadcrumbsService
  ) {
    this.initData();
    this.minimalControls$ = this.popupService.notify$.pipe(
      map(() => !this.popupService.hasGlobalDialog),
      startWith(true)
    );
    this.minimalControls$.pipe(untilDestroyed(this)).subscribe((m) => {
      this.showMinimal = m;
      this.cdr.markForCheck();
    });
    this.disableMinimize$ = this.window?.isFullScreen$.pipe(map((isFullScreen) => isFullScreen && this.isMac));
    this.invertMaximizeIcon$ = this.isMac ? this.window?.isFullScreen$ : this.window?.maximized$;
    this.initAltListener();
    this.isEmbed = isEmbed();
    this.collectionsService.currentCollection$.pipe(untilDestroyed(this)).subscribe((c) => {
      this.currentCollection = c;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.calcBreadcrumbsVisibility();
      }, 0);
    });
  }

  ngOnInit(): void {
    this.embedService?.isExternalWebSite().then(async (v) => {
      const opt = await firstValueFrom(this.embedService.options$);
      if (opt.popup?.closeButton != false)
        this.ACTION_HEADER_BUTTONS.unshift({ type: 'close', icon: 'Windows-close', toolTip: 'ESC to close' });
      if (!v) this.ACTION_HEADER_BUTTONS.unshift({ type: 'openNewWindow', icon: 'launch', toolTip: 'Open in new window' });
    });

    this.routerService.activeRoute$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.checkActiveRoute(res);
      const prev = this.showFullMode;
      this.showFullMode = res.routeConfig.data?.parent === this.settingsPage || (prev ? false : prev);
      if (prev !== this.showFullMode) {
        this.cdr.markForCheck();
      }
    });
    this.navTreeService.currentNode$.pipe(untilDestroyed(this)).subscribe((node) => {
      this.currentLocation = node?.id;
      this.cdr.markForCheck();
    });
    this.workspaceService.ownerOrAdmin$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.isOwnerOrAdmin = value;
      this.cdr.markForCheck();
    });
    this.hubService.stateWithParams$(['ap']).subscribe(() => {
      this.groupName = this.hubService.activePage;
      this.cdr.markForCheck();
    });
    this.homePageService.stickyHeader$.pipe(distinctUntilChanged(), untilDestroyed(this), debounceTime(200)).subscribe((val) => {
      this.homeStickyHeader = val;
      this.cdr.markForCheck();
    });

    this.checkEmbed();
  }

  private checkActiveRoute(res) {
    this.isChatPage = res.data?._value.id === CHAT_PAGE_PATH;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    this.calcBreadcrumbsVisibility();
    windowSizeObserver(50)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.calcBreadcrumbsVisibility();
      });
  }

  private calcBreadcrumbsVisibility() {
    const currentRef = this.currentCollection ? this.collectionHeaderLineRef : null;
    if (!this.breadcrumbs?.nativeElement || !currentRef?.nativeElement) return;
    this.hideBreadcrumbsPath =
      this.breadcrumbs.nativeElement.getBoundingClientRect().right > currentRef.nativeElement.getBoundingClientRect().left;
    this.breadcrumbsService.show = !this.hideBreadcrumbsPath;
    this.cdr.markForCheck();
  }

  async checkEmbed() {
    if (!this.isEmbed) {
      return;
    }
    this.embedInline = await this.embedService?.isInline();
    const options = await firstValueFrom(this.embedService.options$);
    const sidebar = options?.popup?.sidebar || options?.sidebar;
    this.allowBackButtonsInEmbed = this.embedInline && sidebar?.style !== 'hidden';

    const isFullApp = await this.embedService?.isFullApp();
    if (isFullApp) {
      this.showArrowsBreadcrumbs = true;
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initData() {
    /*  
  TODO: remove - app title bar doesnt have pin anymore
  this.window?.pinned$.pipe(untilDestroyed(this)).subscribe((p) => {
      this.pinned = p;
      this.cdr.markForCheck();
    });*/
    this.window?.maximized$.pipe(untilDestroyed(this)).subscribe((m) => {
      this.maximized = m;
      this.cdr.markForCheck();
    });
    this.showArrowsBreadcrumbs = this.isNative;
    this.cdr.markForCheck();
  }

  /** On Mac-OS while alt is clicked the 'full-screen' button turns into maximize */
  private initAltListener() {
    if (!this.isMac) return;

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        takeUntil(this.destroy$),
        filter((ev) => isKey(ev, 'alt'))
      )
      .subscribe(() => this.altDown$.next(true));

    fromEvent<KeyboardEvent>(document, 'keyup')
      .pipe(
        takeUntil(this.destroy$),
        filter((ev) => isKey(ev, 'alt'))
      )
      .subscribe(() => this.altDown$.next(false));

    this.altDown$.pipe(takeUntil(this.destroy$)).subscribe((a) => (this.altDown = a));
  }

  showMinimal: boolean;

  @HostBinding('style.display') get isDisplayed() {
    return this.controls ? 'block' : 'none';
  }

  switchToWindow() {
    this.appService.switchToStandard();
  }

  onActionClick(type: ActionButtonType) {
    switch (type) {
      case 'close':
        this.embedService?.hide();
        break;
      case 'openNewWindow':
        this.embedService.openUrl(this.routerService.currentFullUrl);
        break;
      case 'preferences':
        this.embedService.openUrl('settings/preferences');
        break;
      default:
        break;
    }
  }

  goBack() {
    const currentLocation = this.hubService.currentLocation;
    if (currentLocation.includes(`${this.ASSISTANT_PAGE}/`)) {
      this.routerService.navigateByUrl('/search?ap=assistants');
      return;
    }
    if (currentLocation.startsWith(CHAT_PAGE_PATH)) {
      this.routerService.navigateByUrl('/search');
      return;
    }
    const removed = this.hubService.removeActivePage();
    if (!removed) {
      this.routerService.back();
      this.hubService.clearState();
    }
  }
}
